import React, { useState } from 'react';
import { Box, CircularProgress, Popover, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { getUserDetails } from '../../utils/orgName';
import TopPerformersCard from './TopPerformersCard';
import ProgramsDuePanel from './ProgramsDuePanel';
import { useLearnerGamification } from './useLearnerGamification';
import useCapabilityRelease from '../../hooks/useCapabilityRelease';
import type { ProgramDueItem } from './gamificationTypes';
import { fadeInSx } from '../ui/DashboardSkeletons';

// Admin gamification bar — admin counterpart of LearnerGamificationBar, and
// styled to match it: one outlined panel, top-left, holding the chips whose
// detail opens in on-demand flyouts. The pillars stay the hero of the page.
// Rationale: element intensity backfires (Eger et al. 2024) and admins keep
// control of when detail appears.
//
// Chips: Programs Due · Performers, inline in the Learning Framework header.
// (The tier moved into the Development Tier panel, and setup progress into
// Elliott's options list.)
// (A4 quiz event deferred to the 'Live learning events & content drops'
// workstream, Aug 2026. 'This week' retired in favour of My Tasks, which
// lives as a coming-soon item in Elliott's orb menu.)

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const BORDER_CONTRAST = '#444444';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const TEXT_TERTIARY = '#868686';
const BRAND_LIGHT = '#F79420';
const DUE_RED = '#DA2828';
const TRACK = '#2A2A2A';

const chipSx = {
  // Each chip appears once its data loads; ease it in rather than popping.
  ...fadeInSx,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  height: 32,
  px: '12px',
  borderRadius: '999px',
  bgcolor: SURFACE,
  border: `1px solid ${BORDER_DIM}`,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  fontSize: 12.5,
  color: TEXT_SECONDARY,
  whiteSpace: 'nowrap',
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: '#565656' }
} as const;

const ghostButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  px: '12px',
  py: '6px',
  borderRadius: '8px',
  cursor: 'pointer',
  border: `1px solid ${BORDER_CONTRAST}`,
  color: TEXT_SECONDARY,
  fontFamily: 'Inter, sans-serif',
  fontSize: 12.5,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  transition: 'border-color 0.15s ease, color 0.15s ease',
  '&:hover': { borderColor: BRAND_LIGHT, color: TEXT_PRIMARY }
} as const;

// Shared flyout title — same brandy eyebrow as the learner panels.
const panelTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  color: BRAND_LIGHT,
  lineHeight: 1,
  whiteSpace: 'nowrap'
} as const;

const popoverPaperSx = {
  bgcolor: 'transparent',
  boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
  borderRadius: '8px',
  mt: '10px',
  // The pointer sits above the panel's top edge — don't clip it.
  overflow: 'visible'
} as const;

// Caret at the top of a flyout, aimed back at the chip that opened it.
const PanelPointer: React.FC<{ color?: string }> = ({ color = BORDER_DIM }) => (
  <Box
    sx={{
      position: 'absolute',
      top: -6,
      left: 20,
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: `6px solid ${color}`
    }}
  />
);

type FlyoutKey = 'due' | 'top';

// Completion rate colour — red below a third, amber below two thirds.
const completionColor = (pct: number) => (pct < 34 ? DUE_RED : pct < 67 ? BRAND_LIGHT : '#22C55E');

interface Props {
  /** Org Admins see the whole organization; Program/Group Admins see only the
      learners in the programs they administer. Changes the tier panel copy. */
  audience?: 'orgAdmin' | 'groupAdmin';
  /** Completion rate per program name (0–100) from the dashboard's own
      program list — shown against each program in the Programs Due panel. */
  completionByProgram?: Record<string, number>;
  /** The programs this admin runs that learners owe, from the dashboard's own
      program list (already narrowed to the selected Teams). Not the learner
      summary's list: that's the admin's own enrolments as a learner. */
  programsDue: ProgramDueItem[];
}

const AdminGamificationBar: React.FC<Props> = ({ completionByProgram, programsDue }) => {
  const navigate = useNavigate();
  const [flyout, setFlyout] = useState<{ key: FlyoutKey; anchor: HTMLElement } | null>(null);

  // Top performers come from the BE summary endpoint the learner bar uses, so
  // both dashboards show the same board, ranked across the whole org. No dummy
  // fallbacks: the chip hides until real data arrives (placeholder numbers
  // shipped as real data once — RDP, Sep 2026).
  const { summary } = useLearnerGamification();

  // Top Performers stay org-wide whatever Team is selected: the summary
  // endpoint has no team dimension, and nothing ties a performer to a Team
  // attribute value. A fabricated per-team re-ranking of the org board was
  // tried here and removed — real people must never be shown in an order the
  // data doesn't support. Needs a team-scoped leaderboard from the BE.
  // With the Capability release the Top 3 moves to the Activity tab: a medal on
  // each measure row (DevelopmentTierPanel's MeasureTop3), so each measure shows
  // its own leaders (utils/capabilityRelease.ts).
  const capabilityRelease = useCapabilityRelease();
  const topPerformers = capabilityRelease ? null : summary?.topPerformers ?? null;

  // Org Members lives under the Auth0 org id; orgDetails may be unhydrated on
  // a direct navigation, so fall back to the JWT claim (same as OrgMembersPage).
  const reduxAuthOrgId = useAppSelector((state) => state.orgDetails.data?.authId);
  const authOrgId = reduxAuthOrgId || getUserDetails().orgId;

  const openFlyout = (key: FlyoutKey) => (e: React.MouseEvent<HTMLElement>) =>
    setFlyout({ key, anchor: e.currentTarget });

  // Review Progress — Org Members, pre-filtered to this program. The dropdown
  // is keyed by program id, so the page resolves the name once its program
  // list loads.
  const reviewProgress = (programName: string) => {
    setFlyout(null);
    navigate(`/app/org/${authOrgId}/members?program=${encodeURIComponent(programName)}`);
  };

  return (
    // Outlined panel: same treatment as the learner bar, so the two dashboards
    // read as one system.
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: 36,
        // The chips are one row of stats and must read as one row at any zoom.
        // Wrapping put half of them on a second line as soon as the header got
        // tight, which is what the Elliott bubble beside it now absorbs instead
        // (it shrinks; this bar does not).
        flexWrap: 'nowrap',
        flexShrink: 0,
        '& > *': { flexShrink: 0 },
        // Inline chips in the Learning Framework header — no frame of their own.
        boxSizing: 'border-box'
      }}
    >

      {/* Programs Due — red exclamation, same chip as the learner bar. */}
      {programsDue.length > 0 && (
        <Box onClick={openFlyout('due')} sx={{ ...chipSx, borderColor: 'rgba(218,40,40,0.5)' }}>
          <Box
            component="span"
            sx={{
              width: 15,
              height: 15,
              borderRadius: '50%',
              bgcolor: DUE_RED,
              color: '#FDFDFD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1
            }}
          >
            !
          </Box>
          <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
            {programsDue.length}
          </Box>
        </Box>
      )}

      {/* Performers — org leaderboard; no "You" row on the admin side. */}
      {topPerformers && (
        <Box onClick={openFlyout('top')} sx={chipSx}>
          <span aria-hidden>🏅</span>
          <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
            Top 3
          </Box>
        </Box>
      )}

      <Popover
        open={flyout !== null}
        anchorEl={flyout?.anchor ?? null}
        onClose={() => setFlyout(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        {/* Same panel the learner sees, plus the admin-only completion rate
            and Review Progress CTA. */}
        {flyout?.key === 'due' && (
          <ProgramsDuePanel
            items={programsDue}
            completionByProgram={completionByProgram}
            onReviewProgress={reviewProgress}
          />
        )}

        {flyout?.key === 'top' && topPerformers && (
          <Box sx={{ position: 'relative', width: 'max-content', minWidth: 360, maxWidth: 'calc(100vw - 48px)' }}>
            <PanelPointer />
            <TopPerformersCard data={topPerformers} showYou={false} />
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default AdminGamificationBar;
